import React from 'react';
import * as Styled from './styled';
import { useLocale } from 'locale';
import { IThumbnail, PoiId } from 'utils/entities';
import { OverlayWrapper } from 'components/overlay-wrapper';
import { ImageItem } from '../images-container/image-item';
import Share from 'components/navigation/share';
import FavoritesToggler from 'components/favorites/FavoritesToggler';
import RightIcon from 'assets/svg/right-negative.svg';
import { isNil, isNull } from 'lodash';
import ScrollIntoView from 'scroll-into-view';
import FullScreenSlider from '../full-screen-view/slider';
import FullScreenCarousel from '../full-screen-view/carousel';
import SideBarContact from 'components/contact/side-bar';
import { useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { ContactAgentModalSource } from 'store/state/app';
import ConnectedPOIGalleryStickyContactEntry from 'components/contact/poi-gallery-sticky';
import { NavigateToFn, State as RouteState } from 'config/routes';

interface EventViewPhotoGalleryOpenProps {
  image: IThumbnail;
  index: number;
  total: number;
}

export interface AllPhotosListProps {
  images: IThumbnail[];
  id: PoiId;
  copyText?: string;
  selectedImageIdx: number | null;
  route?: RouteState;
  navigateTo?: NavigateToFn;
  onClose: () => void;
  openReportListingPopup?: () => void;
  eventViewPhotoGalleryOpen?: (opts: EventViewPhotoGalleryOpenProps) => void;
}

export const AllPhotosList: React.FC<AllPhotosListProps> = (props) => {
  const {
    eventViewPhotoGalleryOpen,
    openReportListingPopup,
    selectedImageIdx,
    copyText,
    onClose,
    images,
    id,
    route,
    navigateTo,
  } = props;

  const { t } = useLocale();
  const screenBreakpoint = useScreenBreakpoint();
  const constructImageURI = useImageUrlBuilder();

  const fullScreenIndex = !isNil(route.params.galleryFullscreenIndex) ? route.params.galleryFullscreenIndex : null;

  function setFullScreenIndex(index: number | null) {
    // close fullscreen and gallery entirely, restoring the scroll position
    if (isNil(index) && route.params.activeListingImage === -1 && window.history.length > 1) {
      window.history.back();
    }
    else {
      navigateTo(route.name, {
        ...route.params,
        galleryFullscreenIndex: !isNil(index) ? index : undefined,
      }, { replace: true });
    }
  }

  React.useEffect(() => {
    if (!isNull(selectedImageIdx)) {
      const targetScrollEl = document.querySelector<HTMLElement>(`[data-list-image="idx-${selectedImageIdx}"]`);
      ScrollIntoView(targetScrollEl, {
        time: 300,
        align: {
          top: 0.2,
        },
      });
    }
  }, [ selectedImageIdx ]);

  if (isNull(selectedImageIdx)) return null;

  const mqFrom1to3 = screenBreakpoint <= 3;
  const FullScreenGallery = screenBreakpoint < 3 ? FullScreenCarousel : FullScreenSlider;
  const isOpenFullScreen = !isNull(fullScreenIndex);
  const handleFullScreen = (opts: EventViewPhotoGalleryOpenProps) => {
    eventViewPhotoGalleryOpen(opts);
    setFullScreenIndex(opts.index);
  };

  return (
    <OverlayWrapper>
      <Styled.Header>
        <Styled.BackBtn onClick={onClose}>
          <RightIcon width={16} height={16} />
          {t('unitPage.gallery.back')}
        </Styled.BackBtn>
        <Styled.ExtraBtnWrapper>
          <Share tooltip={false} copyText={copyText} alignment="right" isRound />
          <FavoritesToggler id={id} type="bulletin" tooltip={false} withButton noText isRound />
        </Styled.ExtraBtnWrapper>
      </Styled.Header>
      <Styled.Root>
        <Styled.Main>
          {images.map((image, index) => (
            <div key={`${image.url}-${index}`} data-list-image={`idx-${index}`}>
              <ImageItem
                url={constructImageURI(image.url, { height: 1280 })}
                height={400}
                handleClick={() => handleFullScreen({ image, index, total: images.length })}
                style={{ borderRadius: '8px' }}
                rotateDegrees={image.rotateDegrees}
              />
            </div>
          ))}
        </Styled.Main>
        {mqFrom1to3 ? <ConnectedPOIGalleryStickyContactEntry id={id} /> : (
          <Styled.SideBar>
            <SideBarContact sidePaddingSpacing={1.5} source={ContactAgentModalSource.AllGalleryList} id={id}>
              <Styled.Hint onClick={openReportListingPopup}>{t('reportListing.text')}</Styled.Hint>
            </SideBarContact>
          </Styled.SideBar>
        )}
      </Styled.Root>
      {isOpenFullScreen ? (
        <FullScreenGallery
          images={images}
          copyText={copyText}
          id={id}
          onClose={() => setFullScreenIndex(null)}
          initialIndex={fullScreenIndex}
        />
      ) : null}
    </OverlayWrapper>
  );
};
